import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import connectToSocket from "../../services/socket-io";
import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Typography } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  tabContainer: {
    overflowY: "auto",
    minWidth: 500,
    maxHeight: 350,
    ...theme.scrollbarStyles,
  },
  popoverPaper: {
    width: "100%",
    maxWidth: 540,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    borderRadius: 8,
    [theme.breakpoints.down("sm")]: {
      maxWidth: 270,
    },
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 10,
  },
  noShadow: { boxShadow: "none !important" },
  iconButton: { color: theme.palette.icons.gray },
}));

const NotificationsPopOver = () => {
  const classes = useStyles();

  const history = useHistory();
  const { user } = useContext(AuthContext);

  const ticketIdUrl = +history.location.pathname.split("/")[2];
  const ticketIdRef = useRef(ticketIdUrl);
  const anchorEl = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [desktopNotifications, setDesktopNotifications] = useState([]);

  const { tickets } = useTickets({ withUnreadMessages: "true" });

  // som
  const [play] = useSound(alertSound, {
    volume: 1.0,
    interrupt: true,
    soundEnabled: true,
  });
  const soundAlertRef = useRef();
  const audioReadyRef = useRef(false); // marcamos quando o áudio foi “desbloqueado”

  const socketRef = useRef(null);
  const notificationTimeoutRef = useRef(null);

  const historyRef = useRef(history);

  // Desbloquear áudio no primeiro gesto do usuário (autoplay policies)
  useEffect(() => {
    const resumeAudio = () => {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          if (ctx.state === "suspended") ctx.resume();
          setTimeout(() => ctx.close(), 400);
        }
        audioReadyRef.current = true;
      } catch {}
      window.removeEventListener("click", resumeAudio);
      window.removeEventListener("keydown", resumeAudio);
    };
    window.addEventListener("click", resumeAudio);
    window.addEventListener("keydown", resumeAudio);
    return () => {
      window.removeEventListener("click", resumeAudio);
      window.removeEventListener("keydown", resumeAudio);
    };
  }, []);

  // carregar som / permission de Notification
  useEffect(() => {
    soundAlertRef.current = play;

    // pré-carregar áudio
    const el = new Audio(alertSound);
    el.preload = "auto";
    el.load();

    if ("Notification" in window) {
      Notification.requestPermission();
    } else {
      console.log("[DIAGNÓSTICO][NotificationsPopOver] Browser sem Notification API");
    }
  }, [play]);

  // atualizar lista local quando hook de tickets muda
  useEffect(() => {
    setNotifications(tickets);
    console.log(
      "[DIAGNÓSTICO][NotificationsPopOver] Notificações atualizadas:",
      tickets.length,
      "tickets"
    );
  }, [tickets]);

  // manter referência do ticket atual
  useEffect(() => {
    ticketIdRef.current = ticketIdUrl;
  }, [ticketIdUrl]);

  // conectar socket (instância isolada)
  useEffect(() => {
    if (!user) return;

    const socket = connectToSocket("notifications");
    socketRef.current = socket;

    const handleTicket = (data) => {
      console.log("[NotificationsPopOver] ticket:", data);
      if (data.action === "updateUnread" || data.action === "delete") {
        setNotifications((prev) => prev.filter((t) => t.id !== data.ticketId));
        setDesktopNotifications((prev) => {
          const n = prev.find((n) => n.tag === String(data.ticketId));
          if (n) n.close();
          return prev.filter((n) => n.tag !== String(data.ticketId));
        });
      }
    };

    const handleAppMessage = (data) => {
      console.log("[NotificationsPopOver] appMessage:", data);
      if (
        data.action === "create" &&
        data.message &&
        !data.message.read &&
        (data.ticket.userId === user?.id || !data.ticket.userId)
      ) {
        setNotifications((prev) => {
          const idx = prev.findIndex((t) => t.id === data.ticket.id);
          if (idx !== -1) {
            const copy = [...prev];
            copy[idx] = data.ticket;
            return copy;
          }
          return [data.ticket, ...prev];
        });

        /*const shouldNotNotify =
          (data.message.ticketId === ticketIdRef.current &&
            document.visibilityState === "visible") ||
          (data.ticket.userId && data.ticket.userId !== user?.id) ||
          data.ticket.isGroup;*/

		   const shouldNotNotify =
		   data.message?.fromMe ||                                   // não tocar para mensagens minhas
		    (data.ticket.userId && data.ticket.userId !== user?.id) ||// outro atendente
		    data.ticket.isGroup;    

        if (!shouldNotNotify) {
          if (notificationTimeoutRef.current) {
            clearTimeout(notificationTimeoutRef.current);
          }
          notificationTimeoutRef.current = setTimeout(() => {
            handleNotifications(data);
          }, 300);
        }
      }
    };

    const handleConnect = () => {
      console.log(
        "[NotificationsPopOver] conectado, entrando na sala notification"
      );
      socket.emit("joinNotification");
    };

    socket.on("connect", handleConnect);
    socket.on("ticket", handleTicket);
    socket.on("appMessage", handleAppMessage);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("ticket", handleTicket);
      socket.off("appMessage", handleAppMessage);
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
        notificationTimeoutRef.current = null;
      }
    };
  }, [user]);

  // NOTAS: deixei handleNotifications assíncrona para poder aguardar fallback
  const handleNotifications = async (data) => {
    const { message, contact, ticket } = data;
    console.log(
      "[DIAGNÓSTICO][NotificationsPopOver] Criando notificação para:",
      {
        contactName: contact.name,
        ticketId: ticket.id,
        messagePreview: message.body?.substring(0, 20),
      }
    );

    // Notificação desktop
    try {
      const options = {
        body: `${message.body} - ${format(new Date(), "HH:mm")}`,
        icon: contact.profilePicUrl,
        tag: ticket.id,
        renotify: true,
      };
      const notification = new Notification(
        `${i18n.t("tickets.notification.message")} ${contact.name}`,
        options
      );
      notification.onclick = (e) => {
        e.preventDefault();
        window.focus();
        historyRef.current.push(`/tickets/${ticket.id}`);
      };
      setDesktopNotifications((prev) => {
        const i = prev.findIndex((n) => n.tag === notification.tag);
        if (i !== -1) {
          const copy = [...prev];
          copy[i] = notification;
          return copy;
        }
        return [notification, ...prev];
      });
    } catch (err) {
      console.error(
        "[DIAGNÓSTICO][NotificationsPopOver] Erro ao criar notificação desktop:",
        err
      );
    }

    // Som com fallback
    console.log(
      "[DIAGNÓSTICO][NotificationsPopOver] Reproduzindo som de alerta"
    );

    let played = false;

    // 1) tentar useSound
    try {
      const maybePromise = play();
      played = true;
      if (maybePromise && typeof maybePromise.then === "function") {
        const guard = new Promise((res) => setTimeout(res, 250));
        await Promise.race([maybePromise, guard]);
      }
      console.log(
        "[DIAGNÓSTICO][NotificationsPopOver] useSound acionado com sucesso"
      );
    } catch (err) {
      console.error(
        "[DIAGNÓSTICO][NotificationsPopOver] useSound play() falhou:",
        err
      );
      played = false;
    }

    // 2) fallback nativo (autoplay bloqueado / guia oculta / sem gesto)
    if (
      !played ||
      !audioReadyRef.current ||
      document.hidden ||
      !navigator.userActivation?.hasBeenActive
    ) {
      try {
        const el = new Audio(alertSound);
        el.volume = 1.0;
        await el.play();
        console.log(
          "[DIAGNÓSTICO][NotificationsPopOver] Fallback nativo tocou som"
        );
        played = true;
      } catch (err) {
        console.error(
          "[DIAGNÓSTICO][NotificationsPopOver] Fallback nativo falhou:",
          err
        );
      }
    }

    if (!played) {
      console.warn(
        "[DIAGNÓSTICO][NotificationsPopOver] Nenhum mecanismo conseguiu tocar som (provável autoplay bloqueado/aba mutada/saída de áudio)."
      );
    }
  };

  const handleClick = () => setIsOpen((prev) => !prev);
  const handleClickAway = () => setIsOpen(false);

  const NotificationTicket = ({ children }) => (
    <div onClick={handleClickAway}>{children}</div>
  );

  return (
    <>
      <IconButton
        onClick={handleClick}
        ref={anchorEl}
        aria-label="Open Notifications"
        className={classes.iconButton}
      >
        <Badge badgeContent={notifications.length} color="secondary">
          <ChatIcon />
        </Badge>
      </IconButton>

      <Popover
        disableScrollLock
        open={isOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        classes={{ paper: classes.popoverPaper }}
        onClose={handleClickAway}
      >
        <Typography color="primary">
          {i18n.t("notifications.notificationPopOverTitle")}
        </Typography>
        <List dense className={classes.tabContainer}>
          {notifications.length === 0 ? (
            <ListItem>
              <ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
            </ListItem>
          ) : (
            notifications.map((ticket) => (
              <NotificationTicket key={ticket.id}>
                <TicketListItem ticket={ticket} />
              </NotificationTicket>
            ))
          )}
        </List>
      </Popover>
    </>
  );
};

export default NotificationsPopOver;
